import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { CssBaseline, useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";

import "./styles/animations.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () => {
      const isLight = mode === "light";
      const brandPrimary = isLight
        ? (primaryColorLight || "#5B6CFF")
        : (primaryColorDark || "#6D63FF");

      const tokens = isLight
        ? {
            background: "#eef3fb",
            backgroundAux: "#f6f9ff",
            surface: "rgba(255, 255, 255, 0.84)",
            surfaceSolid: "#ffffff",
            surfaceAlt: "#edf2ff",
            textPrimary: "#111a2f",
            textSecondary: "#58627a",
            border: "rgba(128, 150, 190, 0.22)",
            topBar: "rgba(246, 250, 255, 0.75)",
            menuBg: "rgba(255, 255, 255, 0.78)",
            glowA: "rgba(79, 90, 255, 0.2)",
            glowB: "rgba(25, 196, 255, 0.16)",
            success: "#00B894",
            warning: "#F2A93B",
            error: "#EF476F",
          }
        : {
            background: "#070b16",
            backgroundAux: "#0f1630",
            surface: "rgba(16, 24, 44, 0.76)",
            surfaceSolid: "#121d36",
            surfaceAlt: "#101a31",
            textPrimary: "#e7edff",
            textSecondary: "#9aa7c8",
            border: "rgba(120, 151, 255, 0.2)",
            topBar: "rgba(11, 17, 33, 0.82)",
            menuBg: "rgba(12, 20, 38, 0.78)",
            glowA: "rgba(95, 108, 255, 0.24)",
            glowB: "rgba(0, 219, 255, 0.16)",
            success: "#2EE6A6",
            warning: "#FFB84D",
            error: "#FF547C",
          };

      return createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: 10,
              height: 10,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isLight ? "#c6d3f7" : "#2d3f6b",
              borderRadius: 999,
              border: `2px solid ${isLight ? "#eef3fb" : "#0f1630"}`,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isLight ? "#d8e1fb" : "#2a385d",
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          palette: {
            type: mode,
            primary: {
              main: brandPrimary,
              light: isLight ? "#7e8bff" : "#8f86ff",
              dark: isLight ? "#384bdb" : "#5447dd",
              contrastText: "#ffffff",
            },
            secondary: {
              main: "#00c2ff",
              light: "#5fe0ff",
              dark: "#00a0d4",
              contrastText: isLight ? "#0a1228" : "#e8f2ff",
            },
            success: { main: tokens.success },
            warning: { main: tokens.warning },
            error: { main: tokens.error },
            background: {
              default: tokens.background,
              paper: tokens.surface,
            },
            text: {
              primary: tokens.textPrimary,
              secondary: tokens.textSecondary,
            },
            divider: tokens.border,

            textPrimary: tokens.textPrimary,
            borderPrimary: tokens.border,
            dark: { main: isLight ? "#14203d" : "#e6edff" },
            light: { main: isLight ? "#f6f9ff" : "#0b1327" },
            fontColor: tokens.textPrimary,
            tabHeaderBackground: tokens.surfaceAlt,
            optionsBackground: tokens.surface,
            fancyBackground: tokens.background,
            total: tokens.surfaceSolid,
            messageIcons: isLight ? "#5e6b8f" : "#cfd8f9",
            inputBackground: tokens.surface,
            barraSuperior: tokens.topBar,
          },

          typography: {
            fontFamily: [
              "Sora",
              "Inter",
              "Poppins",
              "Roboto",
              "-apple-system",
              "BlinkMacSystemFont",
              '"Segoe UI"',
              "sans-serif",
            ].join(","),
            h1: { fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.12 },
            h2: { fontWeight: 700, letterSpacing: "-0.028em", lineHeight: 1.16 },
            h3: { fontWeight: 650, letterSpacing: "-0.02em", lineHeight: 1.2 },
            h4: { fontWeight: 650, letterSpacing: "-0.015em", lineHeight: 1.25 },
            h5: { fontWeight: 600, letterSpacing: "-0.012em", lineHeight: 1.28 },
            h6: { fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.32 },
            subtitle1: { fontWeight: 500, letterSpacing: "0.01em", lineHeight: 1.5 },
            subtitle2: { fontWeight: 500, letterSpacing: "0.01em", lineHeight: 1.45 },
            body1: { letterSpacing: "0.005em", lineHeight: 1.55 },
            body2: { letterSpacing: "0.004em", lineHeight: 1.52 },
            button: {
              fontWeight: 600,
              textTransform: "none",
              letterSpacing: "0.015em",
            },
          },

          shape: {
            borderRadius: 16,
          },

          shadows: [
            "none",
            "0 6px 18px rgba(8, 16, 34, 0.22)",
            "0 12px 24px rgba(8, 16, 34, 0.24)",
            "0 18px 34px rgba(8, 16, 34, 0.26)",
            "0 24px 44px rgba(8, 16, 34, 0.3)",
            ...Array(20).fill("0 28px 52px rgba(8, 16, 34, 0.32)"),
          ],

          overrides: {
            MuiCssBaseline: {
              "@global": {
                html: {
                  width: "100%",
                  height: "100%",
                  WebkitTextSizeAdjust: "100%",
                },
                body: {
                  width: "100%",
                  height: "100%",
                  background: isLight
                    ? "radial-gradient(circle at 8% -10%, rgba(126, 143, 255, 0.26), transparent 36%), radial-gradient(circle at 92% 4%, rgba(0, 194, 255, 0.2), transparent 32%), linear-gradient(180deg, #f7faff 0%, #edf3fd 62%, #e8eefb 100%)"
                    : "radial-gradient(circle at 12% -8%, rgba(109, 99, 255, 0.28), transparent 38%), radial-gradient(circle at 88% 6%, rgba(0, 194, 255, 0.18), transparent 34%), linear-gradient(180deg, #070b16 0%, #0a1122 42%, #0c152a 100%)",
                  color: tokens.textPrimary,
                  fontFamily: "Sora, Inter, Poppins, Roboto, sans-serif",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                },
                "#root": {
                  width: "100%",
                  height: "100%",
                },
                "*": {
                  boxSizing: "border-box",
                },
                ".sr-only": {
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                },
                "img, video": {
                  maxWidth: "100%",
                  height: "auto",
                },
                "button:focus-visible, [role='button']:focus-visible, a:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible": {
                  outline: `2px solid ${brandPrimary}`,
                  outlineOffset: 2,
                },
                "*::selection": {
                  backgroundColor: isLight ? "rgba(91,108,255,.22)" : "rgba(109,99,255,.35)",
                },
                "@media (prefers-reduced-motion: reduce)": {
                  "*": {
                    animationDuration: "0.001ms !important",
                    animationIterationCount: "1 !important",
                    transitionDuration: "0.001ms !important",
                    scrollBehavior: "auto !important",
                  },
                },
              },
            },

            MuiContainer: {
              root: {
                paddingLeft: "0 !important",
                paddingRight: "0 !important",
                maxWidth: "none !important",
                width: "100% !important",
              },
            },

            MuiPaper: {
              root: {
                backgroundImage: "none",
                backgroundColor: tokens.surface,
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: `1px solid ${tokens.border}`,
                marginLeft: 0,
                marginRight: 0,
                width: "100%",
              },
              rounded: {
                borderRadius: 18,
              },
            },

            MuiCard: {
              root: {
                borderRadius: 18,
                border: `1px solid ${tokens.border}`,
                backgroundColor: tokens.surface,
                boxShadow: `0 22px 44px rgba(6, 14, 30, ${isLight ? 0.16 : 0.34})`,
              },
            },

            MuiButton: {
              root: {
                minHeight: 42,
                borderRadius: 14,
                textTransform: "none",
                fontWeight: 600,
                padding: "10px 16px",
                transition: "all .24s cubic-bezier(.2,.7,.2,1)",
                ["@media (max-width:600px)"]: {
                  minHeight: 44,
                  padding: "10px 14px",
                },
                "&:hover": {
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              },
              containedPrimary: {
                color: "#fff",
                background: isLight
                  ? "linear-gradient(115deg, #5b6cff 0%, #7e5bff 52%, #00c2ff 100%)"
                  : "linear-gradient(115deg, #6d63ff 0%, #8e4fff 52%, #00d7ff 100%)",
                boxShadow: `0 12px 30px ${tokens.glowA}`,
                "&:hover": {
                  boxShadow: `0 16px 36px ${tokens.glowA}`,
                  filter: "brightness(1.04)",
                },
              },
              outlinedPrimary: {
                color: tokens.textPrimary,
                borderColor: tokens.border,
                backgroundColor: isLight ? "rgba(255,255,255,.62)" : "rgba(18,28,53,.52)",
                "&:hover": {
                  backgroundColor: isLight ? "rgba(255,255,255,.9)" : "rgba(24,36,66,.8)",
                },
              },
            },

            MuiInputBase: {
              root: {
                borderRadius: 14,
                backgroundColor: isLight ? "rgba(255,255,255,0.66)" : "rgba(13,21,39,0.7)",
                border: `1px solid ${tokens.border}`,
                transition: "border-color .2s ease, box-shadow .2s ease, background-color .2s ease",
                "&.Mui-focused": {
                  boxShadow: `0 0 0 3px ${tokens.glowB}`,
                  borderColor: brandPrimary,
                  backgroundColor: isLight ? "rgba(255,255,255,0.92)" : "rgba(13,21,39,0.9)",
                },
              },
              input: {
                color: tokens.textPrimary,
                "&::placeholder": {
                  color: tokens.textSecondary,
                  opacity: 0.9,
                },
              },
            },

            MuiOutlinedInput: {
              notchedOutline: {
                borderColor: "transparent",
              },
            },

            MuiIconButton: {
              root: {
                borderRadius: 12,
                padding: 8,
                ["@media (max-width:600px)"]: {
                  padding: 10,
                },
                "&:focus-visible": {
                  outline: `2px solid ${brandPrimary}`,
                  outlineOffset: 2,
                },
              },
              sizeSmall: {
                padding: 6,
              },
            },

            MuiTab: {
              root: {
                textTransform: "none",
                minHeight: 44,
                fontWeight: 600,
                borderRadius: 12,
                color: tokens.textSecondary,
                transition: "all .2s ease",
                "&.Mui-selected": {
                  color: tokens.textPrimary,
                  background: isLight
                    ? "rgba(255,255,255,0.86)"
                    : "rgba(22,35,64,0.88)",
                },
              },
            },

            MuiTabs: {
              indicator: {
                height: 3,
                borderRadius: 999,
                background: "linear-gradient(90deg, #6d63ff, #00c2ff)",
              },
            },

            MuiAppBar: {
              root: {
                boxShadow: "none",
                backgroundColor: "transparent",
              },
            },

            MuiDrawer: {
              paper: {
                border: `1px solid ${tokens.border}`,
                backgroundColor: tokens.menuBg,
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              },
            },

            MuiMenu: {
              paper: {
                borderRadius: 14,
                border: `1px solid ${tokens.border}`,
                boxShadow: `0 20px 40px rgba(8, 16, 34, ${isLight ? 0.18 : 0.42})`,
              },
            },

            MuiPopover: {
              paper: {
                borderRadius: 14,
                border: `1px solid ${tokens.border}`,
              },
            },

            MuiDialog: {
              paper: {
                borderRadius: 18,
                border: `1px solid ${tokens.border}`,
                backdropFilter: "blur(12px)",
              },
            },

            MuiTableCell: {
              root: {
                borderBottom: `1px solid ${tokens.border}`,
              },
              head: {
                fontWeight: 650,
                color: tokens.textPrimary,
                backgroundColor: isLight ? "rgba(243,247,255,.9)" : "rgba(17,28,52,.8)",
              },
            },

            MuiChip: {
              root: {
                borderRadius: 999,
                fontWeight: 600,
              },
            },

            MuiTooltip: {
              tooltip: {
                borderRadius: 10,
                backgroundColor: isLight ? "#12213f" : "#1a2a4d",
                color: "#e9f1ff",
                fontSize: 12,
              },
              touch: {
                borderRadius: 10,
                backgroundColor: isLight ? "#12213f" : "#1a2a4d",
              },
            },
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      );
    },
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0000FF");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#39ACE7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(
          file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Multi100");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName("Multi100");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
